import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import leadService from '../../../api/crm/leads/leadService';
import { useLeads } from '../../../context/LeadContext';
import {
  Users,
  Plus,
  Download,
  Eye,
  Edit,
  Trash2,
  X,
  UserPlus,
  MessageCircle,
  CheckCircle,
  Award,
  XCircle,
  UserCheck,
  UserX,
  ClipboardList,
  ClipboardCheck,
  Target,
  Filter,
  Briefcase,
  Trophy,
  Ban,
  Activity,
  BadgeCheck,
  CalendarCheck,
  CircleDot,
  Clock,
  FileCheck,
  Flag,
  Globe,
  Phone,
  PhoneCall,
  Footprints,
  Hourglass,
  PenTool,
  Presentation,
  Send,
  Share2,
  Search,
  ShieldCheck,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronDown,
  Info,
  TrendingUp,
  ArrowUpRight,
  LayoutDashboard
} from 'lucide-react';
import {
  LineChart,
  Line,
  ResponsiveContainer
} from 'recharts';
import {
  sanitizeSearchInput,
  handleSearchInputChange,
  getCharacterCount,
  FIELD_LIMITS
} from '../../../utils/inputValidation';
import {
  FacebookIcon,
  InstagramIcon,
  LinkedInIcon,
  GoogleIcon,
  WhatsAppIcon
} from './LeadSourceIcons';
import Button from '../../../components/common/Button/Button';
import { useAuth } from '../../../context/AuthContext';
import teamManagementService from '../../../api/crm/team-management/teamManagementService';
import { toast, Toaster } from 'react-hot-toast';
import PermissionGate from '../../../components/common/PermissionGate';
import { formatIndianAmount, formatIndianNumber } from '../../../utils/amountFormatter';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import LeadSelectionModal from './LeadSelectionModal';

const STATUS_OUTCOME_VALUES = new Set(['closed_won', 'closed_lost']);

const EXACT_STATUS_VISUALS = {
  new: { icon: UserPlus, color: 'bg-emerald-500', iconBg: 'bg-emerald-50 text-emerald-600 border border-emerald-100', borderColor: 'border-emerald-200/80 hover:border-emerald-400', lineColor: '#10B981', change: '↑ Fresh Inquiries', changeColor: 'text-emerald-600' },
  contacted: { icon: MessageCircle, color: 'bg-amber-500', iconBg: 'bg-amber-50 text-amber-600 border border-amber-100', borderColor: 'border-amber-200/80 hover:border-amber-400', lineColor: '#F59E0B', change: '↑ In Discussions', changeColor: 'text-amber-600' },
  qualified: { icon: CheckCircle, color: 'bg-purple-500', iconBg: 'bg-purple-50 text-purple-600 border border-purple-100', borderColor: 'border-purple-200/80 hover:border-purple-400', lineColor: '#8B5CF6', change: '↑ Verified Prospects', changeColor: 'text-purple-600' },
  converted: { icon: Award, color: 'bg-green-600', iconBg: 'bg-emerald-50 text-emerald-700 border border-emerald-100', borderColor: 'border-emerald-200/80 hover:border-emerald-400', lineColor: '#059669', change: '↑ Won Conversions', changeColor: 'text-emerald-600' },
  disqualified: { icon: XCircle, color: 'bg-red-500', iconBg: 'bg-rose-50 text-rose-600 border border-rose-100', borderColor: 'border-rose-200/80 hover:border-rose-400', lineColor: '#EF4444', change: '↓ Dropped Leads', changeColor: 'text-rose-500' },
  closed_won: { icon: Trophy, color: 'bg-emerald-600', iconBg: 'bg-emerald-50 text-emerald-600 border border-emerald-100', borderColor: 'border-emerald-200/80 hover:border-emerald-400', lineColor: '#10B981', change: '↑ Closed Won', changeColor: 'text-emerald-600' },
  closed_lost: { icon: Ban, color: 'bg-slate-500', iconBg: 'bg-slate-100 text-slate-600 border border-slate-200', borderColor: 'border-slate-200 hover:border-slate-400', lineColor: '#64748B', change: '↓ Closed Lost', changeColor: 'text-slate-500' }
};

const STATUS_KEYWORD_VISUALS = [
  { keywords: ['new', 'fresh', 'created', 'open'], icon: UserPlus, color: 'bg-emerald-500', iconBg: 'bg-emerald-50 text-emerald-600 border border-emerald-100', borderColor: 'border-emerald-200/80 hover:border-emerald-400', lineColor: '#10B981', change: '↑ Fresh Inquiries', changeColor: 'text-emerald-600' },
  { keywords: ['suspect', 'contact', 'called', 'call', 'phone', 'reached'], icon: PhoneCall, color: 'bg-amber-500', iconBg: 'bg-amber-50 text-amber-600 border border-amber-100', borderColor: 'border-amber-200/80 hover:border-amber-400', lineColor: '#F59E0B', change: '↑ Contacted', changeColor: 'text-amber-600' },
  { keywords: ['discussion', 'conversation', 'talk', 'connect'], icon: MessageCircle, color: 'bg-cyan-500', iconBg: 'bg-cyan-50 text-cyan-600 border border-cyan-100', borderColor: 'border-cyan-200/80 hover:border-cyan-400', lineColor: '#06B6D4', change: '↑ Engaged', changeColor: 'text-cyan-600' },
  { keywords: ['prospect', 'qualified', 'qualification', 'fit', 'eligible'], icon: CheckCircle, color: 'bg-purple-500', iconBg: 'bg-purple-50 text-purple-600 border border-purple-100', borderColor: 'border-purple-200/80 hover:border-purple-400', lineColor: '#8B5CF6', change: '↑ Qualified Fit', changeColor: 'text-purple-600' },
  { keywords: ['demo', 'presentation', 'meeting', 'schedule', 'scheduled'], icon: Presentation, color: 'bg-indigo-500', iconBg: 'bg-indigo-50 text-indigo-600 border border-indigo-100', borderColor: 'border-indigo-200/80 hover:border-indigo-400', lineColor: '#6366F1', change: '↑ Demo Scheduled', changeColor: 'text-indigo-600' },
  { keywords: ['proposal', 'quote', 'quotation', 'document', 'sent'], icon: FileCheck, color: 'bg-sky-600', iconBg: 'bg-sky-50 text-sky-600 border border-sky-100', borderColor: 'border-sky-200/80 hover:border-sky-400', lineColor: '#0284C7', change: '↑ Proposal Sent', changeColor: 'text-sky-600' },
  { keywords: ['negotiation', 'negotiate', 'progress', 'followup', 'follow up'], icon: TrendingUp, color: 'bg-teal-600', iconBg: 'bg-teal-50 text-teal-600 border border-teal-100', borderColor: 'border-teal-200/80 hover:border-teal-400', lineColor: '#0D9488', change: '↑ In Negotiation', changeColor: 'text-teal-600' },
  { keywords: ['pending', 'waiting', 'hold', 'on hold'], icon: Hourglass, color: 'bg-orange-500', iconBg: 'bg-orange-50 text-orange-600 border border-orange-100', borderColor: 'border-orange-200/80 hover:border-orange-400', lineColor: '#F97316', change: '• Pending Action', changeColor: 'text-orange-600' },
  { keywords: ['review', 'approval', 'approved', 'verified'], icon: BadgeCheck, color: 'bg-violet-500', iconBg: 'bg-violet-50 text-violet-600 border border-violet-100', borderColor: 'border-violet-200/80 hover:border-violet-400', lineColor: '#8B5CF6', change: '↑ Verified', changeColor: 'text-violet-600' },
  { keywords: ['target', 'priority', 'important'], icon: Target, color: 'bg-rose-500', iconBg: 'bg-rose-50 text-rose-500 border border-rose-100', borderColor: 'border-rose-200/80 hover:border-rose-400', lineColor: '#F43F5E', change: '↑ Priority', changeColor: 'text-rose-500' },
  { keywords: ['converted', 'conversion'], icon: Award, color: 'bg-green-600', iconBg: 'bg-emerald-50 text-emerald-700 border border-emerald-100', borderColor: 'border-emerald-200/80 hover:border-emerald-400', lineColor: '#059669', change: '↑ Converted', changeColor: 'text-emerald-600' },
  { keywords: ['won', 'win', 'success', 'closed won'], icon: Trophy, color: 'bg-emerald-600', iconBg: 'bg-emerald-50 text-emerald-600 border border-emerald-100', borderColor: 'border-emerald-200/80 hover:border-emerald-400', lineColor: '#10B981', change: '↑ Won Deal', changeColor: 'text-emerald-600' },
  { keywords: ['lost', 'closed lost'], icon: Ban, color: 'bg-slate-500', iconBg: 'bg-slate-100 text-slate-600 border border-slate-200', borderColor: 'border-slate-200 hover:border-slate-400', lineColor: '#64748B', change: '↓ Lost Deal', changeColor: 'text-slate-500' },
  { keywords: ['disqualified', 'reject', 'rejected', 'invalid'], icon: XCircle, color: 'bg-red-500', iconBg: 'bg-rose-50 text-rose-600 border border-rose-100', borderColor: 'border-rose-200/80 hover:border-rose-400', lineColor: '#EF4444', change: '↓ Disqualified', changeColor: 'text-rose-500' },
  { keywords: ['compliance', 'validation', 'validated'], icon: ShieldCheck, color: 'bg-blue-500', iconBg: 'bg-blue-50 text-blue-600 border border-blue-100', borderColor: 'border-blue-200/80 hover:border-blue-400', lineColor: '#3B82F6', change: '↑ Compliant', changeColor: 'text-blue-600' },
  { keywords: ['submitted', 'send'], icon: Send, color: 'bg-blue-500', iconBg: 'bg-blue-50 text-blue-600 border border-blue-100', borderColor: 'border-blue-200/80 hover:border-blue-400', lineColor: '#3B82F6', change: '↑ Submitted', changeColor: 'text-blue-600' },
  { keywords: ['activity', 'active'], icon: Activity, color: 'bg-fuchsia-500', iconBg: 'bg-fuchsia-50 text-fuchsia-600 border border-fuchsia-100', borderColor: 'border-fuchsia-200/80 hover:border-fuchsia-400', lineColor: '#D946EF', change: '↑ Active Logs', changeColor: 'text-fuchsia-600' },
  { keywords: ['calendar', 'date'], icon: CalendarCheck, color: 'bg-indigo-500', iconBg: 'bg-indigo-50 text-indigo-600 border border-indigo-100', borderColor: 'border-indigo-200/80 hover:border-indigo-400', lineColor: '#6366F1', change: '↑ Scheduled', changeColor: 'text-indigo-600' },
  { keywords: ['flag', 'milestone'], icon: Flag, color: 'bg-orange-500', iconBg: 'bg-orange-50 text-orange-600 border border-orange-100', borderColor: 'border-orange-200/80 hover:border-orange-400', lineColor: '#F97316', change: '↑ Milestone', changeColor: 'text-orange-600' },
  { keywords: ['checklist', 'task'], icon: ClipboardCheck, color: 'bg-violet-500', iconBg: 'bg-violet-50 text-violet-600 border border-violet-100', borderColor: 'border-violet-200/80 hover:border-violet-400', lineColor: '#8B5CF6', change: '↑ Task Done', changeColor: 'text-violet-600' },
  { keywords: ['time', 'delay'], icon: Clock, color: 'bg-cyan-500', iconBg: 'bg-cyan-50 text-cyan-600 border border-cyan-100', borderColor: 'border-cyan-200/80 hover:border-cyan-400', lineColor: '#06B6D4', change: '↑ In Progress', changeColor: 'text-cyan-600' }
];

const STATUS_FALLBACK_VISUALS = [
  { icon: CircleDot, color: 'bg-blue-500', iconBg: 'bg-blue-50 text-blue-600 border border-blue-100', borderColor: 'border-blue-200/80 hover:border-blue-400', lineColor: '#3B82F6', change: '↑ Active', changeColor: 'text-blue-600' },
  { icon: Flag, color: 'bg-orange-500', iconBg: 'bg-orange-50 text-orange-600 border border-orange-100', borderColor: 'border-orange-200/80 hover:border-orange-400', lineColor: '#F97316', change: '• Monitored', changeColor: 'text-orange-600' },
  { icon: Activity, color: 'bg-fuchsia-500', iconBg: 'bg-fuchsia-50 text-fuchsia-600 border border-fuchsia-100', borderColor: 'border-fuchsia-200/80 hover:border-fuchsia-400', lineColor: '#D946EF', change: '↑ Active', changeColor: 'text-fuchsia-600' },
  { icon: Briefcase, color: 'bg-sky-600', iconBg: 'bg-sky-50 text-sky-600 border border-sky-100', borderColor: 'border-sky-200/80 hover:border-sky-400', lineColor: '#0284C7', change: '↑ Projects', changeColor: 'text-sky-600' },
  { icon: BadgeCheck, color: 'bg-violet-500', iconBg: 'bg-violet-50 text-violet-600 border border-violet-100', borderColor: 'border-violet-200/80 hover:border-violet-400', lineColor: '#8B5CF6', change: '↑ Verified', changeColor: 'text-violet-600' },
  { icon: Target, color: 'bg-rose-500', iconBg: 'bg-rose-50 text-rose-500 border border-rose-100', borderColor: 'border-rose-200/80 hover:border-rose-400', lineColor: '#F43F5E', change: '↑ Targeted', changeColor: 'text-rose-500' }
];

const getDynamicStatusVisual = (status, index) => {
  if (EXACT_STATUS_VISUALS[status.value]) return EXACT_STATUS_VISUALS[status.value];

  const searchableText = `${status.value || ''} ${status.name || ''}`.toLowerCase().replace(/[_-]/g, ' ');
  const matched = STATUS_KEYWORD_VISUALS.find(option =>
    option.keywords.some(keyword => searchableText.includes(keyword))
  );

  return matched || STATUS_FALLBACK_VISUALS[index % STATUS_FALLBACK_VISUALS.length];
};

const getSparklineData = (val, isDown = false) => {
  const num = parseFloat(val) || 0;
  if (num === 0) {
    return [{ v: 0.5 }, { v: 0.8 }, { v: 0.4 }, { v: 0.9 }, { v: 0.6 }, { v: 1.0 }];
  }
  if (isDown) {
    return [
      { v: num },
      { v: Math.max(0.5, num * 0.85) },
      { v: Math.max(0.5, num * 0.7) },
      { v: Math.max(0.5, num * 0.5) },
      { v: Math.max(0.2, num * 0.3) }
    ];
  }
  return [
    { v: Math.max(0.5, num * 0.3) },
    { v: Math.max(0.5, num * 0.55) },
    { v: Math.max(0.5, num * 0.45) },
    { v: Math.max(0.5, num * 0.8) },
    { v: Math.max(0.5, num * 0.7) },
    { v: num }
  ];
};

const LeadDashboard = ({ onViewLead, onAddLead }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser } = useAuth();

  const handleViewLead = (leadId) => {
    if (onViewLead) {
      onViewLead(leadId);
    } else {
      navigate(`/lead_detail_360/${leadId}`);
    }
  };

  const handleAddLead = () => {
    if (onAddLead) {
      onAddLead();
    } else {
      navigate('/manual_lead_entry');
    }
  };

  // Search input ref
  const searchInputRef = useRef(null);
  const isInitialMount = useRef(true);

  // Lead context for pagination
  const { leads, loading: leadsLoading, error: leadsError, loadLeads, pagination, clearError, deleteLead } = useLeads();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Lead List Filters state (matching LeadListFilters.jsx)
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    source: 'all',
    status: 'all',
    score: 'all',
    owner: 'all',
    region: 'all',
    dateRange: 'all'
  });

  // Sorting state
  const [sortColumn, setSortColumn] = useState('updatedAt');
  const [sortDirection, setSortDirection] = useState('desc');

  // State for delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [leadToDelete, setLeadToDelete] = useState(null);

  // Filtering state
  const [showAllData, setShowAllData] = useState(() => {
    const saved = localStorage.getItem('crm_lead_dashboard_all_data_toggle');
    return saved ? JSON.parse(saved) : false;
  });
  const [selectedManager, setSelectedManager] = useState('');
  const [selectedSalesExecutive, setSelectedSalesExecutive] = useState('');
  const [managers, setManagers] = useState([]);
  const [salesExecutives, setSalesExecutives] = useState([]);

  // Searchable dropdown state
  const [managerSearchOpen, setManagerSearchOpen] = useState(false);
  const [managerSearchTerm, setManagerSearchTerm] = useState('');
  const [debouncedManagerSearch, setDebouncedManagerSearch] = useState('');
  const [selectedManagerData, setSelectedManagerData] = useState(null);

  const [executiveSearchOpen, setExecutiveSearchOpen] = useState(false);
  const [executiveSearchTerm, setExecutiveSearchTerm] = useState('');
  const [debouncedExecutiveSearch, setDebouncedExecutiveSearch] = useState('');
  const [selectedExecutiveData, setSelectedExecutiveData] = useState(null);

  const [selectedPeriod, setSelectedPeriod] = useState('all_time');
  const [customDateRange, setCustomDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [showLeadSelectionModal, setShowLeadSelectionModal] = useState(false);

  const [leadStats, setLeadStats] = useState([
    // Lead Pipeline Cards (Row 1)
    { title: 'Total Leads', value: '0', icon: Users, color: 'bg-blue-500' },
    { title: 'New Leads', value: '0', icon: UserPlus, color: 'bg-emerald-500' },
    { title: 'Suspect', value: '0', icon: MessageCircle, color: 'bg-amber-500' },
    // Lead Pipeline Cards (Row 2)
    { title: 'Prospect', value: '0', icon: CheckCircle, color: 'bg-purple-500' },
    { title: 'Converted', value: '0', icon: Award, color: 'bg-green-600' },
    { title: 'Disqualified', value: '0', icon: XCircle, color: 'bg-red-500' },
    // Assignment Cards (Row 3)
    { title: 'Assigned Leads', value: '0', icon: UserCheck, color: 'bg-cyan-500' },
    { title: 'Unassigned Leads', value: '0', icon: UserX, color: 'bg-orange-500' },
    { title: 'Assignment Rules', value: '0', icon: ClipboardList, color: 'bg-indigo-500' },
    // Rules Cards (Row 4)
    { title: 'Scoring Rules', value: '0', icon: Target, color: 'bg-rose-500' },
    { title: 'Qualification Rules', value: '0', icon: Filter, color: 'bg-violet-500' },
    // Opportunity & Activity Cards (Row 4)
    { title: 'Won', value: '0', icon: Trophy, color: 'bg-emerald-600' },
    { title: 'Lost', value: '0', icon: Ban, color: 'bg-slate-500' },
    { title: 'Total Activities', value: '0', icon: Activity, color: 'bg-fuchsia-500' }
  ]);
  // Initialize source data as empty - will be populated from database
  const [sourceData, setSourceData] = useState([]);
  const [dashboardStats, setDashboardStats] = useState({
    totalLeads: 0,
    newLeads: 0,
    contactedLeads: 0,
    qualifiedLeads: 0,
    convertedLeads: 0,
    disqualifiedLeads: 0,
    assignedLeads: 0,
    unassigned: 0,
    assignmentRulesCount: 0,
    scoringRulesCount: 0,
    qualificationCriteriaCount: 0,
    openOpportunities: 0,
    closedWonOpportunities: 0,
    closedLostOpportunities: 0,
    fixedCostProjects: 0,
    recurringCostProjects: 0,
    fixedCostProjectsTotalCost: 0,
    recurringCostProjectsTotalCost: 0
  });


  // Debounce search terms
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedManagerSearch(managerSearchTerm), 300);
    return () => clearTimeout(timer);
  }, [managerSearchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedExecutiveSearch(executiveSearchTerm), 300);
    return () => clearTimeout(timer);
  }, [executiveSearchTerm]);

  // Query for Managers
  const { data: managersData } = useQuery({
    queryKey: ['crmManagers', debouncedManagerSearch],
    queryFn: () => teamManagementService.getCRMUsers({
      roleFilter: 'sales',
      search: debouncedManagerSearch
    }),
    enabled: currentUser?.role === 'super_admin' || (currentUser?.role === 'sales_manager' && showAllData)
  });

  useEffect(() => {
    if (managersData?.success) {
      const salesManagers = managersData.users.filter(u => u.role === 'sales_manager');
      setManagers(salesManagers);
    }
  }, [managersData]);

  // Query for Sales Executives
  const managerIdForExecutives = (currentUser?.role === 'super_admin' || (currentUser?.role === 'sales_manager' && showAllData))
    ? (selectedManager || undefined)
    : (currentUser?._id || currentUser?.id || '');

  const { data: executivesData } = useQuery({
    queryKey: ['salesExecutives', managerIdForExecutives, debouncedExecutiveSearch],
    queryFn: () => teamManagementService.getCRMUsers({
      managerId: managerIdForExecutives,
      search: debouncedExecutiveSearch
    }),
    enabled: !!currentUser
  });

  useEffect(() => {
    if (executivesData?.success) {
      const executives = executivesData.users.filter(u => u.role === 'sales_executive');
      setSalesExecutives(executives);
    } else if (executivesData) {
      setSalesExecutives([]);
    }
  }, [executivesData]);

  // Dashboard Filters
  const dashboardFilters = React.useMemo(() => {
    const f = {};
    // Use user IDs for lead filtering (not employee IDs)
    if (selectedManagerData?.user) f.managerId = selectedManagerData.user;
    if (selectedExecutiveData?.user) f.salesExecutiveId = selectedExecutiveData.user;

    if (currentUser?.role === 'sales_manager') {
      if (!showAllData) {
        f.managerId = currentUser.id || currentUser._id;
      }
    } else if (currentUser?.role === 'sales_executive') {
      f.salesExecutiveId = currentUser.id || currentUser._id;
    }

    // Date filtering
    if (customDateRange.startDate || customDateRange.endDate) {
      if (customDateRange.startDate) f.startDate = customDateRange.startDate;
      if (customDateRange.endDate) f.endDate = customDateRange.endDate;
      f.range = 'custom';
    } else {
      f.range = selectedPeriod;
    }

    f.allData = showAllData;

    return f;
  }, [selectedManagerData, selectedExecutiveData, currentUser, selectedPeriod, customDateRange, showAllData]);

  // Map frontend source to backend source (directly map dynamically)
  const mapSourceToBackend = (source) => {
    if (!source) return 'manual_entry';
    return typeof source === 'string' ? source.toLowerCase().replace(/\s+/g, '_') : source;
  };

  // Map frontend status to backend stage (needed before useEffect)
  const mapStatusToBackend = (status) => {
    const statusMap = {
      'New': 'new',
      'Suspect': 'contacted',
      'Prospect': 'qualified',
      'Converted': 'converted',
      'Disqualified': 'disqualified',
      'Proposal Sent': 'proposal_sent',
      'Negotiation': 'negotiation',
      'Closed Won': 'closed_won',
      'Closed Lost': 'closed_lost'
    };
    return statusMap[status] || status?.toLowerCase() || 'new';
  };

  // Query for Statistics
  const { data: statsData, isLoading: isLoadingStats } = useQuery({
    queryKey: ['leadStatistics', dashboardFilters],
    queryFn: () => leadService.getLeadStatistics(dashboardFilters),
    enabled: !!currentUser
  });

  // Load leads using useLeads context for pagination (matching LeadListFilters)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (currentUser) {
        const apiFilters = {
          ...dashboardFilters,
          page: currentPage,
          limit: itemsPerPage,
          search: searchTerm || undefined,
          source: filters.source !== 'all' ? mapSourceToBackend(filters.source) : undefined,
          stage: filters.status !== 'all' ? mapStatusToBackend(filters.status) : undefined
        };

        // Remove undefined values
        Object.keys(apiFilters).forEach(key =>
          apiFilters[key] === undefined && delete apiFilters[key]
        );


        loadLeads(apiFilters);
      }
    }, isInitialMount.current ? 0 : 500); // Load immediately on mount, debounce on subsequent changes

    // Mark that initial mount is complete after setting up the first load
    if (isInitialMount.current) {
      isInitialMount.current = false;
    }

    return () => clearTimeout(timeoutId);
  }, [currentUser, dashboardFilters, currentPage, itemsPerPage, searchTerm, filters.source, filters.status, loadLeads]);

  // Update stats state when query data changes
  useEffect(() => {
    if (statsData?.success && statsData.statistics) {
      const stats = statsData.statistics;

      const totalLeadsCount = typeof stats.totalLeads === 'number' ? stats.totalLeads : parseInt(stats.totalLeads) || 0;
      const newLeadsCount = typeof stats.newLeads === 'number' ? stats.newLeads : parseInt(stats.newLeads) || 0;
      const contactedCount = typeof stats.contactedLeads === 'number' ? stats.contactedLeads : parseInt(stats.contactedLeads) || 0;
      const qualifiedCount = typeof stats.qualifiedLeads === 'number' ? stats.qualifiedLeads : parseInt(stats.qualifiedLeads) || 0;
      const convertedCount = typeof stats.convertedLeads === 'number' ? stats.convertedLeads : parseInt(stats.convertedLeads) || 0;
      const disqualifiedCount = typeof stats.disqualifiedLeads === 'number' ? stats.disqualifiedLeads : parseInt(stats.disqualifiedLeads) || 0;
      const assignedCount = typeof stats.assignedLeads === 'number' ? stats.assignedLeads : parseInt(stats.assignedLeads) || 0;
      const unassignedCount = typeof stats.unassigned === 'number' ? stats.unassigned : parseInt(stats.unassigned) || 0;
      const assignmentRulesCount = typeof stats.assignmentRulesCount === 'number' ? stats.assignmentRulesCount : parseInt(stats.assignmentRulesCount) || 0;
      const scoringRulesCount = typeof stats.scoringRulesCount === 'number' ? stats.scoringRulesCount : parseInt(stats.scoringRulesCount) || 0;
      const qualificationRulesCount = typeof stats.qualificationCriteriaCount === 'number' ? stats.qualificationCriteriaCount : parseInt(stats.qualificationCriteriaCount) || 0;
      const openOppsCount = typeof stats.openOpportunities === 'number' ? stats.openOpportunities : parseInt(stats.openOpportunities) || 0;
      const closedWonCount = typeof stats.closedWonOpportunities === 'number' ? stats.closedWonOpportunities : parseInt(stats.closedWonOpportunities) || 0;
      const closedLostCount = typeof stats.closedLostOpportunities === 'number' ? stats.closedLostOpportunities : parseInt(stats.closedLostOpportunities) || 0;
      const fixedCostProjectsCount = typeof stats.fixedCostProjects === 'number' ? stats.fixedCostProjects : parseInt(stats.fixedCostProjects) || 0;
      const recurringCostProjectsCount = typeof stats.recurringCostProjects === 'number' ? stats.recurringCostProjects : parseInt(stats.recurringCostProjects) || 0;
      const fixedCostProjectsTotalCost = typeof stats.fixedCostProjectsTotalCost === 'number' ? stats.fixedCostProjectsTotalCost : parseFloat(stats.fixedCostProjectsTotalCost) || 0;
      const recurringCostProjectsTotalCost = typeof stats.recurringCostProjectsTotalCost === 'number' ? stats.recurringCostProjectsTotalCost : parseFloat(stats.recurringCostProjectsTotalCost) || 0;
      const totalActivitiesCount = typeof stats.totalActivities === 'number' ? stats.totalActivities : parseInt(stats.totalActivities) || 0;

      const fallbackStatusData = [
        { name: 'New Leads', value: 'new', count: newLeadsCount },
        { name: 'Suspect', value: 'contacted', count: contactedCount },
        { name: 'Prospect', value: 'qualified', count: qualifiedCount },
        { name: 'Converted', value: 'converted', count: convertedCount },
        { name: 'Disqualified', value: 'disqualified', count: disqualifiedCount },
        { name: 'Won', value: 'closed_won', count: closedWonCount },
        { name: 'Lost', value: 'closed_lost', count: closedLostCount }
      ];

      const statusCards = (Array.isArray(stats.statusData) && stats.statusData.length > 0 ? stats.statusData : fallbackStatusData)
        .map((status, index) => {
          const visual = getDynamicStatusVisual(status, index);

          return {
            title: status.name || status.value,
            value: String(status.count || 0),
            icon: visual.icon,
            color: visual.color,
            iconBg: visual.iconBg || 'bg-emerald-50 text-emerald-600 border border-emerald-100',
            borderColor: visual.borderColor || 'border-emerald-200/80 hover:border-emerald-400',
            lineColor: visual.lineColor || '#10B981',
            change: visual.change || '↑ Active',
            changeColor: visual.changeColor || 'text-emerald-600',
            description: status.description || '',
            statusValue: status.value
          };
        });
      const outcomeStatusCards = statusCards.filter(status => STATUS_OUTCOME_VALUES.has(status.statusValue));
      const pipelineStatusCards = statusCards.filter(status => !STATUS_OUTCOME_VALUES.has(status.statusValue));

      setLeadStats([
        { title: 'Total Leads', value: totalLeadsCount.toString(), icon: Users, color: 'bg-blue-500', iconBg: 'bg-blue-50 text-blue-600 border border-blue-100', borderColor: 'border-blue-200/80 hover:border-blue-400', lineColor: '#3B82F6', change: '↑ Active Pipeline', changeColor: 'text-blue-600' },
        ...pipelineStatusCards,
        { title: 'Assigned Leads', value: assignedCount.toString(), icon: UserCheck, color: 'bg-cyan-500', iconBg: 'bg-cyan-50 text-cyan-600 border border-cyan-100', borderColor: 'border-cyan-200/80 hover:border-cyan-400', lineColor: '#06B6D4', change: '↑ Team Allocated', changeColor: 'text-cyan-600' },
        { title: 'Unassigned Leads', value: unassignedCount.toString(), icon: UserX, color: 'bg-orange-500', iconBg: 'bg-orange-50 text-orange-600 border border-orange-100', borderColor: 'border-orange-200/80 hover:border-orange-400', lineColor: '#F97316', change: '• Pending Routing', changeColor: 'text-orange-600' },
        { title: 'Assignment Rules', value: assignmentRulesCount.toString(), icon: ClipboardList, color: 'bg-indigo-500', iconBg: 'bg-indigo-50 text-indigo-600 border border-indigo-100', borderColor: 'border-indigo-200/80 hover:border-indigo-400', lineColor: '#6366F1', change: 'Automated Routing', changeColor: 'text-indigo-600' },
        { title: 'Scoring Rules', value: scoringRulesCount.toString(), icon: Target, color: 'bg-rose-500', iconBg: 'bg-rose-50 text-rose-500 border border-rose-100', borderColor: 'border-rose-200/80 hover:border-rose-400', lineColor: '#F43F5E', change: 'Score Matrix Active', changeColor: 'text-rose-500' },
        { title: 'Qualification Rules', value: qualificationRulesCount.toString(), icon: Filter, color: 'bg-violet-500', iconBg: 'bg-violet-50 text-violet-600 border border-violet-100', borderColor: 'border-violet-200/80 hover:border-violet-400', lineColor: '#8B5CF6', change: 'Criteria Verified', changeColor: 'text-violet-600' },
        { title: 'Fixed Cost Projects', value: fixedCostProjectsCount.toString(), totalCost: fixedCostProjectsTotalCost, icon: Briefcase, color: 'bg-sky-600', iconBg: 'bg-sky-50 text-sky-600 border border-sky-100', borderColor: 'border-sky-200/80 hover:border-sky-400', lineColor: '#0284C7', change: 'Fixed Project Value', changeColor: 'text-sky-600' },
        { title: 'Recurring Cost Projects', value: recurringCostProjectsCount.toString(), totalCost: recurringCostProjectsTotalCost, icon: TrendingUp, color: 'bg-teal-600', iconBg: 'bg-teal-50 text-teal-600 border border-teal-100', borderColor: 'border-teal-200/80 hover:border-teal-400', lineColor: '#0D9488', change: 'Recurring Revenue', changeColor: 'text-teal-600' },
        ...outcomeStatusCards,
        { title: 'Total Activities', value: totalActivitiesCount.toString(), icon: Activity, color: 'bg-fuchsia-500', iconBg: 'bg-fuchsia-50 text-fuchsia-600 border border-fuchsia-100', borderColor: 'border-fuchsia-200/80 hover:border-fuchsia-400', lineColor: '#D946EF', change: '↑ Activity Logged', changeColor: 'text-fuchsia-600' }
      ]);

      if (stats.sourceData && Array.isArray(stats.sourceData)) {
        setSourceData(stats.sourceData);
      } else {
        setSourceData([]);
      }

      setDashboardStats({
        totalLeads: totalLeadsCount,
        newLeads: newLeadsCount,
        contactedLeads: contactedCount,
        qualifiedLeads: qualifiedCount,
        convertedLeads: convertedCount,
        disqualifiedLeads: disqualifiedCount,
        assignedLeads: assignedCount,
        unassigned: unassignedCount,
        assignmentRulesCount: assignmentRulesCount,
        scoringRulesCount: scoringRulesCount,
        qualificationCriteriaCount: qualificationRulesCount,
        openOpportunities: openOppsCount,
        closedWonOpportunities: closedWonCount,
        closedLostOpportunities: closedLostCount,
        fixedCostProjects: fixedCostProjectsCount,
        recurringCostProjects: recurringCostProjectsCount,
        fixedCostProjectsTotalCost,
        recurringCostProjectsTotalCost,
        totalActivities: totalActivitiesCount,
        statusData: stats.statusData || fallbackStatusData
      });
    }
  }, [statsData]);

  // Unified loading state
  const isLoading = isLoadingStats || leadsLoading;

  // Handle click outside for dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (managerSearchOpen && !event.target.closest('.manager-dropdown')) {
        setManagerSearchOpen(false);
        setManagerSearchTerm('');
      }
      if (executiveSearchOpen && !event.target.closest('.executive-dropdown')) {
        setExecutiveSearchOpen(false);
        setExecutiveSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [managerSearchOpen, executiveSearchOpen]);

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Helper function to check if lead is assigned to current user and get display text
  const getOwnerDisplayText = (lead) => {
    const ownerName = lead.owner || 'Unassigned';
    if (ownerName === 'Unassigned') return ownerName;

    // Check if the lead is assigned to the current user
    const currentUserId = currentUser?.id || currentUser?._id;
    const assignedToId = lead.assignedTo?._id || lead.assignedTo;

    // Also check by comparing names if IDs don't match
    const currentUserFullName = `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim().toLowerCase();
    const ownerNameLower = ownerName.toLowerCase();

    const isAssignedToCurrentUser = (
      (currentUserId && assignedToId && currentUserId.toString() === assignedToId.toString()) ||
      (currentUserFullName && ownerNameLower && currentUserFullName === ownerNameLower)
    );

    return isAssignedToCurrentUser ? `${ownerName} (You)` : ownerName;
  };

  // Helper function to render owner name with highlighted "(You)" badge
  const renderOwnerWithHighlight = (lead) => {
    const ownerName = lead.owner || 'Unassigned';
    if (ownerName === 'Unassigned') return <span>{ownerName}</span>;

    // Check if the lead is assigned to the current user
    const currentUserId = currentUser?.id || currentUser?._id;
    const assignedToId = lead.assignedTo?._id || lead.assignedTo;

    // Also check by comparing names if IDs don't match
    const currentUserFullName = `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim().toLowerCase();
    const ownerNameLower = ownerName.toLowerCase();

    const isAssignedToCurrentUser = (
      (currentUserId && assignedToId && currentUserId.toString() === assignedToId.toString()) ||
      (currentUserFullName && ownerNameLower && currentUserFullName === ownerNameLower)
    );

    if (isAssignedToCurrentUser) {
      return (
        <>
          {ownerName} <span className="text-emerald-600 font-semibold">(You)</span>
        </>
      );
    }
    return <span>{ownerName}</span>;
  };

  // Helper function to get lead name (matching LeadListFilters)
  const getLeadName = (lead) => {
    if (lead.firstName && lead.lastName) {
      return `${lead.firstName} ${lead.lastName}`;
    } else if (lead.firstName) {
      return lead.firstName;
    } else if (lead.lastName) {
      return lead.lastName;
    } else if (lead.name) {
      return lead.name;
    } else {
      return 'Unknown Lead';
    }
  };

  // Map backend source to frontend display (directly populate from backend resolved values)
  const mapSourceToFrontend = (source) => {
    if (!source) return 'Manual Entry';
    if (typeof source === 'object' && source !== null) {
      return source.name || source.label || 'Manual Entry';
    }
    return source;
  };

  // Check if any filters or search are active (matching LeadListFilters)
  const hasActiveFilters = useMemo(() => {
    return (
      searchTerm.trim() !== '' ||
      filters.source !== 'all' ||
      filters.status !== 'all' ||
      filters.score !== 'all' ||
      filters.owner !== 'all' ||
      filters.region !== 'all' ||
      filters.dateRange !== 'all'
    );
  }, [searchTerm, filters]);

  // Clear all filters and search (matching LeadListFilters)
  const handleClearFilters = () => {
    setSearchTerm('');
    setFilters({
      source: 'all',
      status: 'all',
      score: 'all',
      owner: 'all',
      region: 'all',
      dateRange: 'all'
    });
    setCurrentPage(1);
  };

  // Show delete confirmation modal
  const handleDeleteClick = (leadId) => {
    setLeadToDelete(leadId);
    setShowDeleteModal(true);
  };

  // Close delete modal without deleting
  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setLeadToDelete(null);
  };

  // Confirm delete
  const handleConfirmDelete = async () => {
    if (!leadToDelete) {
      setShowDeleteModal(false);
      return;
    }

    try {
      const result = await deleteLead(leadToDelete);
      if (result.success) {
        toast.success('Lead deleted successfully');
        // Refresh the leads list with current pagination
        await loadLeads({ page: currentPage, limit: itemsPerPage });
      } else {
        toast(result.error || 'Unable to delete lead. Please try again.', {
          icon: '⚠️',
          style: { background: '#f59e0b', color: '#fff' }
        });
      }
    } catch (error) {

      toast('Unable to delete lead. Please check your connection and try again.', {
        icon: '⚠️',
        style: { background: '#f59e0b', color: '#fff' }
      });
    } finally {
      setShowDeleteModal(false);
      setLeadToDelete(null);
    }
  };

  // Handle items per page change
  const handleItemsPerPageChange = (limit) => {
    setItemsPerPage(limit);
    setCurrentPage(1);
  };


  // Use leads directly from API - all filtering and sorting is done server-side
  // This matches LeadListFilters exactly
  const filteredLeads = leads || [];


  const getScoreColor = (score) => {
    switch (score) {
      case 'Hot': return 'bg-red-100 text-red-800';
      case 'Warm': return 'bg-yellow-100 text-yellow-800';
      case 'Cold': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Handle column sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Get sorted leads for the table
  const getSortedLeads = () => {
    if (!sortColumn || !filteredLeads || filteredLeads.length === 0) return filteredLeads;

    return [...filteredLeads].sort((a, b) => {
      let aValue, bValue;

      if (sortColumn === 'name') {
        const aName = getLeadName(a).toLowerCase();
        const bName = getLeadName(b).toLowerCase();
        aValue = aName;
        bValue = bName;
      } else if (sortColumn === 'source') {
        aValue = (a.source || '').toLowerCase();
        bValue = (b.source || '').toLowerCase();
      } else if (sortColumn === 'score') {
        aValue = (a.score || '').toLowerCase();
        bValue = (b.score || '').toLowerCase();
      } else if (sortColumn === 'status') {
        aValue = (a.status || '').toLowerCase();
        bValue = (b.status || '').toLowerCase();
      } else if (sortColumn === 'owner') {
        aValue = (a.owner || '').toLowerCase();
        bValue = (b.owner || '').toLowerCase();
      } else if (sortColumn === 'projectCost') {
        aValue = Number(a.totalCost) || 0;
        bValue = Number(b.totalCost) || 0;
      } else if (sortColumn === 'updatedAt') {
        aValue = new Date(a.updatedAt || a.createdAt || 0).getTime();
        bValue = new Date(b.updatedAt || b.createdAt || 0).getTime();
      } else {
        aValue = a[sortColumn] || '';
        bValue = b[sortColumn] || '';
      }

      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'New': return 'bg-blue-100 text-blue-800';
      case 'Suspect': return 'bg-green-100 text-green-800';
      case 'Prospect': return 'bg-purple-100 text-purple-800';
      case 'Converted': return 'bg-indigo-100 text-indigo-800';
      case 'Disqualified': return 'bg-red-100 text-red-800';
      case 'Proposal Sent': return 'bg-orange-100 text-orange-800';
      case 'Negotiation': return 'bg-yellow-100 text-yellow-800';
      case 'Closed Won': return 'bg-emerald-100 text-emerald-800';
      case 'Closed Lost': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleViewAllLeads = () => {
    navigate('/lead_list_filters');
  };

  const handleExportLeads = () => {
    try {
      // Helper function to escape CSV values
      const escapeCSV = (value) => {
        if (value === null || value === undefined) return '';
        const stringValue = String(value);
        // If value contains comma, quote, or newline, wrap in quotes and escape quotes
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      };

      // Build CSV content - matching exact dashboard format
      const csvLines = [];

      // Add KPI Cards (Total Leads, New Leads, Contacted, Qualified, Converted)
      csvLines.push('Lead Pipeline Metrics');
      csvLines.push('Metric,Value');
      leadStats.forEach(stat => {
        csvLines.push(`${escapeCSV(stat.title)},${escapeCSV(stat.value)}`);
      });

      csvLines.push(''); // Empty line

      // Add Lead Sources with count and percentage
      csvLines.push('Lead Sources Distribution');
      csvLines.push('Source,Count,Percentage');
      if (sourceData && sourceData.length > 0) {
        sourceData.forEach(source => {
          csvLines.push(`${escapeCSV(source.name)},${source.count || 0},${escapeCSV(source.value)}%`);
        });
      } else {
        csvLines.push('No lead sources data available');
      }

      csvLines.push(''); // Empty line

      // Add Recent Leads
      csvLines.push('Name,Company,Email,Source,Score,Status,Owner,Project Cost,Last Activity');
      if (filteredLeads && filteredLeads.length > 0) {
        filteredLeads.forEach(lead => {
          const name = escapeCSV(getLeadName(lead));
          const company = escapeCSV(lead.company);
          const email = escapeCSV(lead.email);
          const source = escapeCSV(lead.source);
          const score = escapeCSV(lead.score || 0); // Use numeric score instead of label
          const status = escapeCSV(lead.status);
          const owner = escapeCSV(lead.owner);
          const projectCost = escapeCSV(lead.totalCost ? `₹${formatIndianAmount(lead.totalCost)}` : '₹0');
          const lastActivity = escapeCSV(formatDate(lead.updatedAt || lead.createdAt));

          csvLines.push(`${name},${company},${email},${source},${score},${status},${owner},${projectCost},${lastActivity}`);
        });
      } else {
        csvLines.push('No recent leads available');
      }

      // Create CSV content
      const csvContent = csvLines.join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `lead_dashboard_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {

      toast('Unable to export dashboard data. Please check your connection and try again.', {
        icon: '⚠️',
        style: { background: '#f59e0b', color: '#fff' }
      });
    }
  };

  const handleViewSourceAnalytics = () => {
    navigate('/lead_dashboard');
  };




  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#0F2942] text-white flex items-center justify-center shadow-xs flex-shrink-0">
            <LayoutDashboard className="w-5 h-5 stroke-[2]" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Lead Dashboard</h1>
            <p className="text-sm sm:text-base text-gray-600 mt-0.5">Monitor lead volume and conversion rates.</p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
          <div className="flex items-center space-x-3">
            {currentUser?.role === 'sales_manager' && (
              <label className="inline-flex items-center cursor-pointer select-none shrink-0">
                <div className="relative">
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={showAllData}
                    onChange={(e) => {
                      const val = e.target.checked;
                      setShowAllData(val);
                      localStorage.setItem('crm_lead_dashboard_all_data_toggle', JSON.stringify(val));
                      setSelectedManager('');
                      setSelectedManagerData(null);
                      setSelectedSalesExecutive('');
                      setSelectedExecutiveData(null);
                    }}
                  />
                  <div className={`w-10 h-5 rounded-full transition-colors duration-200 ease-in-out ${showAllData ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
                  <div className={`absolute left-0.5 top-0.5 bg-white w-4 h-4 rounded-full shadow transform transition-transform duration-200 ease-in-out ${showAllData ? 'translate-x-5' : 'translate-x-0'}`}></div>
                </div>
                <span className="ml-2 text-sm font-medium text-gray-600 whitespace-nowrap">All Data</span>
              </label>
            )}
            {(!customDateRange.startDate && !customDateRange.endDate) && (
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="all_time">All Time</option>
                <option value="today">Today</option>
                <option value="last_7_days">Last 7 days</option>
                <option value="last_30_days">Last 30 days</option>
                <option value="last_60_days">Last 60 days</option>
                <option value="last_90_days">Last 90 days</option>
              </select>
            )}

            <PermissionGate permission="lead_management" action="create">
              <Button
                variant="primary"
                icon={<Plus className="h-4 w-4" />}
                onClick={() => setShowLeadSelectionModal(true)}
                className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
              >
                Log Activity
              </Button>
            </PermissionGate>

            <DateRangeFilter
              startDate={customDateRange.startDate}
              endDate={customDateRange.endDate}
              onChange={(range) => setCustomDateRange(range)}
              allowFutureDates={false}
            />

            {(customDateRange.startDate || customDateRange.endDate) && (
              <Button
                variant="outline"
                onClick={() => {
                  setCustomDateRange({ startDate: '', endDate: '' });
                  setSelectedPeriod('last_30_days');
                }}
              >
                Clear
              </Button>
            )}
          </div>

          <PermissionGate permission="lead_management" action="read">
            <Button
              variant="outline"
              icon={<Download className="h-4 w-4" />}
              onClick={handleExportLeads}
              className="w-full sm:w-auto"
            >
              Export
            </Button>
          </PermissionGate>
          <PermissionGate permission="lead_management" action="create">
            <Button
              variant="primary"
              icon={<Plus className="h-4 w-4" />}
              onClick={() => onAddLead ? onAddLead() : navigate('/manual_lead_entry')}
              className="w-full sm:w-auto"
            >
              Create Lead
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3.5">
        {isLoadingStats && !statsData ? (
          [...Array(5)].map((_, i) => (
            <div key={i} className="bg-white rounded-2xl p-4 shadow-2xs border border-slate-200/80 animate-pulse flex flex-col justify-between h-28">
              <div className="flex items-start justify-between">
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                <div className="h-9 w-9 bg-gray-200 rounded-full"></div>
              </div>
              <div className="h-7 bg-gray-200 rounded w-1/3 mt-2"></div>
            </div>
          ))
        ) : (
          leadStats.map((stat, index) => {
            const IconComponent = stat.icon;

            const customDescriptions = {
              'Total Leads': 'Count of all leads.',
              'Assigned Leads': 'Total leads assigned to you.',
              'Unassigned Leads': 'Total leads that are currently not assigned to any manager or executive.',
              'Assignment Rules': 'Total active assignment rules configured to automatically assign leads to managers or executives.',
              'Scoring Rules': 'Total active scoring rules configured to assign lead scores based on defined conditions and rule sets.',
              'Qualification Rules': 'Total active qualification rules created to automatically mark leads as qualified or disqualified based on configured criteria.',
              'Fixed Cost Projects': 'Total leads marked with fixed project cost.',
              'Recurring Cost Projects': 'Total leads marked with recurring project cost.',
              'Total Activities': 'Total activities performed across all leads, including calls, follow-ups, meetings, and other actions.'
            };

            const description = stat.description || customDescriptions[stat.title];
            const iconBg = stat.iconBg || 'bg-blue-50 text-blue-600 border border-blue-100';
            const borderColor = stat.borderColor || 'border-slate-200 hover:border-blue-400';
            const lineColor = stat.lineColor || '#3B82F6';
            const change = stat.change || '↑ Active';
            const changeColor = stat.changeColor || 'text-emerald-600';
            const isDownTrend = stat.title === 'Disqualified' || stat.title === 'Lost';
            const sparklineData = getSparklineData(stat.value, isDownTrend);

            return (
              <div
                key={index}
                role="button"
                tabIndex={0}
                className={`group relative bg-white p-4 rounded-2xl border ${borderColor} shadow-2xs hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 cursor-pointer flex flex-col justify-between overflow-hidden focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
                onClick={() => {
                  let filterData = { ...filters };

                  if (stat.statusValue) {
                    filterData.status = stat.statusValue;
                    navigate('/lead_list_filters', { state: { filters: filterData } });
                    return;
                  }

                  switch (stat.title) {
                    case 'Assigned Leads':
                      filterData.assignee = 'Assigned';
                      break;
                    case 'Unassigned Leads':
                      filterData.assignee = 'Unassigned';
                      break;
                    case 'Assignment Rules':
                      navigate('/lead_assignment');
                      return;
                    case 'Scoring Rules':
                    case 'Qualification Rules':
                      navigate('/lead_scoring_qualification');
                      return;
                    case 'Fixed Cost Projects':
                      filterData.costType = 'fixed';
                      break;
                    case 'Recurring Cost Projects':
                      filterData.costType = 'recurring';
                      break;
                    case 'Total Activities':
                      navigate('/lead_activities');
                      return;
                    default:
                      // Default to all
                      break;
                  }

                  navigate('/lead_list_filters', { state: { filters: filterData } });
                }}
              >
                <div>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1 flex-wrap">
                        <p className="text-[11px] font-bold text-gray-500 group-hover:text-blue-600 transition-colors leading-tight truncate">
                          {stat.title}
                        </p>
                        <ArrowUpRight className="w-3 h-3 text-slate-300 opacity-0 group-hover:opacity-100 group-hover:text-blue-600 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all flex-shrink-0" />
                        {description && (
                          <div
                            className="group/tooltip relative z-20 hover:z-[9999]"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Info className="h-3.5 w-3.5 text-gray-400 hover:text-blue-500 transition-colors cursor-help" />
                            <div className="absolute left-0 top-full mt-1.5 hidden group-hover/tooltip:block w-52 bg-slate-900/95 backdrop-blur-sm text-white text-xs rounded-xl p-2.5 shadow-xl z-[9999] pointer-events-none break-words normal-case font-normal leading-relaxed border border-slate-700">
                              <p className="font-semibold text-blue-400 mb-1">{stat.title}</p>
                              <p className="first-letter:capitalize text-slate-200">{description}</p>
                            </div>
                          </div>
                        )}
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mt-1.5 group-hover:text-blue-900 transition-colors tracking-tight flex items-baseline gap-1 flex-wrap">
                        {formatIndianNumber(stat.value)}
                        {stat.totalCost !== undefined && (
                          <span className="text-xs font-semibold text-slate-500">
                            (₹{formatIndianNumber(stat.totalCost)})
                          </span>
                        )}
                      </h3>
                    </div>
                    <div className={`w-9 h-9 rounded-full ${iconBg} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-200 shadow-2xs`}>
                      <IconComponent className="w-4 h-4" />
                    </div>
                  </div>

                  <p className={`text-[11px] font-semibold ${changeColor} mt-2 z-10 flex items-center gap-1`}>
                    {change}
                  </p>
                </div>

                <div className="h-8 w-full mt-2 -mb-1 -mx-4 px-4 pointer-events-none opacity-80 group-hover:opacity-100 transition-opacity">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={sparklineData}>
                      <Line type="monotone" dataKey="v" stroke={lineColor} strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 gap-6">
        {/* Source Mix Chart */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 space-y-2 sm:space-y-0">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">Lead Sources</h3>
          </div>
          {/* Source icons mapping */}
          {(() => {
            const sourceIcons = {
              'Website': Globe,
              'Facebook': FacebookIcon,
              'Instagram': InstagramIcon,
              'LinkedIn': LinkedInIcon,
              'Google Ads': GoogleIcon,
              'WhatsApp': WhatsAppIcon,
              'Referral': Share2,
              'Cold Call': Phone,
              'Walk In': Footprints,
              'Manual Entry': PenTool
            };

            const brandColors = {
              'Website': '#2563EB',     // Blue 600
              'Facebook': '#1877F2',    // Brand Facebook
              'Instagram': 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)', // Brand Instagram Gradient
              'LinkedIn': '#0A66C2',    // Brand LinkedIn
              'Google Ads': '#EF4444',  // Brand Google Red (matching capture module)
              'WhatsApp': '#25D366',    // Brand WhatsApp
              'Referral': '#10B981',    // Emerald 500
              'Cold Call': '#F59E0B',   // Amber 500
              'Walk In': '#8B5CF6',     // Violet 500
              'Manual Entry': '#6B7280' // Gray 500
            };

            if (isLoadingStats && !statsData) {
              return (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              );
            }

            return sourceData.length === 0 ? (
              <div className="text-center py-8">
                <Users className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">No lead sources data available yet.</p>
                <p className="text-gray-400 text-xs mt-1">Create leads to see source distribution.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {sourceData.map((source, index) => {
                  const IconComponent = sourceIcons[source.name] || Globe;
                  const brandColor = brandColors[source.name] || '#6B7280';

                  return (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        <div
                          className="p-2 rounded-lg flex-shrink-0"
                          style={{ background: brandColor }}
                        >
                          <IconComponent className="h-4 w-4 text-white" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <span className="text-sm font-medium text-gray-900 block truncate" title={source.name}>{source.name}</span>
                          <span className="text-xs text-gray-500">{formatIndianNumber(source.count || 0)} leads</span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3 flex-shrink-0">
                        <div className="w-20 bg-gray-200 rounded-full h-2">
                          <div
                            className="h-2 rounded-full"
                            style={{
                              width: `${Math.max(source.value, 2)}%`,
                              background: brandColor
                            }}
                          ></div>
                        </div>
                        <span className="text-sm font-bold text-gray-900 w-12 text-right">{source.value}%</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      </div>

      {/* Leads Filters */}
      {(currentUser?.role === 'super_admin' || currentUser?.role === 'sales_manager') && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="mb-4">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">Agent By</h3>
          </div>
          {/* First Row: Selectors and Reset Button */}
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            {(currentUser?.role === 'super_admin' || (currentUser?.role === 'sales_manager' && showAllData)) && (
              <div className="flex-1 w-full sm:w-auto relative manager-dropdown">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Manager
                </label>
                <div
                  onClick={() => setManagerSearchOpen(!managerSearchOpen)}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 bg-white cursor-pointer flex items-center justify-between h-10 hover:border-gray-400"
                >
                  <span className={`text-sm ${selectedManager ? 'text-gray-900' : 'text-gray-500'} truncate`}>
                    {selectedManager ? (selectedManagerData?.name || 'Selected Manager') : 'All Managers'}
                  </span>
                  <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${managerSearchOpen ? 'rotate-180' : ''}`} />
                </div>

                {managerSearchOpen && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg overflow-hidden">
                    <div className="p-2 border-b border-gray-100">
                      <div className="relative">
                        <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Search Managers..."
                          value={managerSearchTerm}
                          onChange={(e) => setManagerSearchTerm(e.target.value)}
                          autoFocus
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    </div>
                    <div className="max-h-60 overflow-y-auto">
                      <div
                        className="px-4 py-2 text-sm hover:bg-blue-50 cursor-pointer text-gray-700"
                        onClick={() => {
                          setSelectedManager('');
                          setSelectedManagerData(null);
                          setSelectedSalesExecutive('');
                          setSelectedExecutiveData(null);
                          setManagerSearchOpen(false);
                          setManagerSearchTerm('');
                        }}
                      >
                        All Managers
                      </div>
                      {managers.map((m) => (
                        <div
                          key={m.id || m._id}
                          className="px-4 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-50 last:border-0"
                          onClick={() => {
                            setSelectedManager(m.id || m._id);
                            setSelectedManagerData(m);
                            setSelectedSalesExecutive('');
                            setSelectedExecutiveData(null);
                            setManagerSearchOpen(false);
                            setManagerSearchTerm('');
                          }}
                        >
                          <div className="text-sm font-medium text-gray-900">{m.name}</div>
                          <div className="text-xs text-gray-500">
                            {m.employeeId ? `${m.employeeId} • ` : ''}{m.email}
                          </div>
                          {m.phone && <div className="text-xs text-gray-400">{m.phone}</div>}
                        </div>
                      ))}
                      {managers.length === 0 && (
                        <div className="px-4 py-4 text-sm text-gray-500 text-center">No managers found</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {(currentUser?.role === 'super_admin' || currentUser?.role === 'sales_manager') && (
              <div className="flex-1 w-full sm:w-auto relative executive-dropdown">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sales Executive
                </label>
                <div
                  onClick={() => {
                    setExecutiveSearchOpen(!executiveSearchOpen);
                  }}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 bg-white cursor-pointer flex items-center justify-between h-10 hover:border-gray-400"
                >
                  <span className={`text-sm ${selectedSalesExecutive ? 'text-gray-900' : 'text-gray-500'} truncate`}>
                    {selectedSalesExecutive ? (selectedExecutiveData?.name || 'Selected Executive') : 'All Sales Executives'}
                  </span>
                  <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${executiveSearchOpen ? 'rotate-180' : ''}`} />
                </div>

                {executiveSearchOpen && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg overflow-hidden">
                    <div className="p-2 border-b border-gray-100">
                      <div className="relative">
                        <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Search Sales Executives..."
                          value={executiveSearchTerm}
                          onChange={(e) => setExecutiveSearchTerm(e.target.value)}
                          autoFocus
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    </div>
                    <div className="max-h-60 overflow-y-auto">
                      <div
                        className="px-4 py-2 text-sm hover:bg-blue-50 cursor-pointer text-gray-700"
                        onClick={() => {
                          setSelectedSalesExecutive('');
                          setSelectedExecutiveData(null);
                          setExecutiveSearchOpen(false);
                          setExecutiveSearchTerm('');
                        }}
                      >
                        All Sales Executives
                      </div>
                      {salesExecutives.map((e) => (
                        <div
                          key={e.id || e._id}
                          className="px-4 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-50 last:border-0"
                          onClick={() => {
                            setSelectedSalesExecutive(e.id || e._id);
                            setSelectedExecutiveData(e);
                            setExecutiveSearchOpen(false);
                            setExecutiveSearchTerm('');
                          }}
                        >
                          <div className="text-sm font-medium text-gray-900">{e.name}</div>
                          <div className="text-xs text-gray-500">
                            {e.employeeId ? `${e.employeeId} • ` : ''}{e.email}
                          </div>
                          {e.phone && <div className="text-xs text-gray-400">{e.phone}</div>}
                        </div>
                      ))}
                      {salesExecutives.length === 0 && (
                        <div className="px-4 py-4 text-sm text-gray-500 text-center">No executives found</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex-1 w-full sm:w-auto">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date Range
              </label>
              <DateRangeFilter
                startDate={customDateRange.startDate}
                endDate={customDateRange.endDate}
                onChange={(range) => setCustomDateRange(range)}
                allowFutureDates={false}
                inputClassName="pl-2 pr-2 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm w-full cursor-pointer h-10"
              />
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSelectedManager('');
                setSelectedManagerData(null);
                setSelectedSalesExecutive('');
                setSelectedExecutiveData(null);
                setManagerSearchTerm('');
                setExecutiveSearchTerm('');
                setCustomDateRange({ startDate: '', endDate: '' });
                setSelectedPeriod('last_30_days');
              }}
              className="h-10 px-4"
            >
              Reset Filters
            </Button>
          </div>

          {/* Second Row: Detailed Agent Info */}
          {(selectedManager || selectedSalesExecutive) && (
            <div className="flex flex-col sm:flex-row gap-4 mt-4 pt-4 border-t border-gray-100">
              {selectedManager && selectedManagerData && (
                <div className="flex-1 p-2 bg-blue-50 rounded-lg border border-blue-100">
                  <div className="text-[10px] text-blue-600 font-bold uppercase tracking-wider mb-1">Selected Manager</div>
                  <div className="text-sm font-semibold text-gray-900">{selectedManagerData.name}</div>
                  <div className="text-xs text-gray-600 truncate">{selectedManagerData.email}</div>
                </div>
              )}
              {selectedSalesExecutive && selectedExecutiveData && (
                <div className="flex-1 p-2 bg-blue-50 rounded-lg border border-blue-100">
                  <div className="text-[10px] text-blue-600 font-bold uppercase tracking-wider mb-1">Selected Sales Executive</div>
                  <div className="text-sm font-semibold text-gray-900">{selectedExecutiveData.name}</div>
                  <div className="text-xs text-gray-600 truncate">{selectedExecutiveData.email}</div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Leads Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {/* Loading State */}
        {leadsLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading leads...</p>
            </div>
          </div>
        )}

        {/* Desktop Table View - Exactly matching LeadListFilters */}
        {!leadsLoading && (
          <div className="hidden lg:block overflow-x-auto w-full">
            <table className="min-w-[1200px] divide-y divide-gray-200 table-auto w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[200px] max-w-[300px] cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center space-x-1">
                      <div className="flex items-center gap-2">
                        <span>Lead Details</span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {formatIndianNumber(pagination?.totalLeads || filteredLeads.length || 0)}
                        </span>
                      </div>
                      {sortColumn === 'name' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px] cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('source')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Source</span>
                      {sortColumn === 'source' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[80px] cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('score')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Score</span>
                      {sortColumn === 'score' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px] cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Status</span>
                      {sortColumn === 'status' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px] cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('owner')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Owner</span>
                      {sortColumn === 'owner' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[130px] cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('projectCost')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Project Cost</span>
                      {sortColumn === 'projectCost' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[140px] cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('updatedAt')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Last Updated</span>
                      {sortColumn === 'updatedAt' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px]">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredLeads.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center">
                      <div className="text-gray-500">
                        <Users className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                        <p className="text-lg font-medium">No leads found</p>
                        <p className="text-sm mt-2">
                          {leads.length === 0
                            ? 'No leads have been created yet. Create your first lead to get started.'
                            : 'Try adjusting your filters to see more results.'}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  getSortedLeads().map((lead) => {
                    const leadId = lead.id || lead._id || lead.leadId;
                    return (
                      <tr key={leadId} className="hover:bg-gray-50">
                        <td className="px-4 py-4 min-w-[200px] max-w-[300px]">
                          <div className="flex items-center space-x-3 min-w-0">
                            <div className="min-w-0 flex-1 overflow-hidden">
                              <div className="text-sm font-medium text-gray-900 truncate capitalize" title={getLeadName(lead)}>{getLeadName(lead)}</div>
                              <div className="text-sm text-gray-500 truncate first-letter:capitalize" title={lead.company || 'No company'}>{lead.company || 'No company'}</div>
                              <div className="text-xs text-gray-400 truncate" title={lead.email || 'No email'}>{lead.email || 'No email'}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap min-w-[100px]">
                          <span className="text-sm text-gray-900 truncate block" title={mapSourceToFrontend(lead.source)}>{mapSourceToFrontend(lead.source)}</span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 min-w-[80px]">
                          {lead.score}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap min-w-[100px]">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusColor(lead.status || 'New')}`}>
                            {lead.status || 'New'}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap min-w-[120px]">
                          <div>
                            <span className="text-sm text-gray-900 first-letter:capitalize truncate block" title={getOwnerDisplayText(lead)}>{renderOwnerWithHighlight(lead)}</span>
                            {lead.ownerEmail && (
                              <span className="text-xs text-gray-500 truncate block" title={lead.ownerEmail}>{lead.ownerEmail}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap min-w-[130px]">
                          <span className="text-sm font-medium text-gray-900">₹{lead.totalCost ? formatIndianAmount(lead.totalCost) : '0'}</span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap min-w-[140px]">
                          <span className="text-sm text-gray-500">{formatDate(lead.updatedAt || lead.createdAt)}</span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium min-w-[100px]">
                          <PermissionGate permission="lead_management" action="read">
                            <button
                              onClick={() => handleViewLead(leadId)}
                              className="text-blue-600 hover:text-blue-900 flex-shrink-0"
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                          </PermissionGate>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Mobile Card View - Exactly matching LeadListFilters */}
        {!leadsLoading && (
          <div className="lg:hidden">
            {filteredLeads.length === 0 ? (
              <div className="p-12 text-center">
                <Users className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <p className="text-lg font-medium text-gray-900 mb-2">No leads found</p>
                <p className="text-sm text-gray-500">
                  {leads.length === 0
                    ? 'No leads have been created yet.'
                    : 'Try adjusting your filters.'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {getSortedLeads().map((lead) => {
                  const leadId = lead.id || lead._id || lead.leadId;
                  return (
                    <div key={leadId} className="p-4 hover:bg-gray-50">
                      <div className="flex items-start space-x-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-2">
                            <h4 className="text-sm font-medium text-gray-900 truncate capitalize">{getLeadName(lead)}</h4>
                            <span className="text-xs font-medium text-gray-900">
                              {lead.score}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 truncate first-letter:capitalize">{lead.company || 'No company'}</p>
                          <p className="text-xs text-gray-400 truncate">{lead.email || 'No email'}</p>

                          <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 mt-3">
                            <div className="flex items-center space-x-1">
                              <span className="text-xs text-gray-500">Source:</span>
                              <span className="text-xs font-medium text-gray-900 truncate" title={mapSourceToFrontend(lead.source)}>{mapSourceToFrontend(lead.source)}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <span className="text-xs text-gray-500">Status:</span>
                              <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusColor(lead.status || 'New')}`}>
                                {lead.status || 'New'}
                              </span>
                            </div>
                          </div>

                          <div className="flex items-center justify-between mt-3">
                            <div className="text-xs text-gray-500">
                              <span className="block">Owner: <span className="first-letter:capitalize">{renderOwnerWithHighlight(lead)}</span></span>
                              <span className="block">Project Cost: ₹{lead.totalCost ? formatIndianAmount(lead.totalCost) : '0'}</span>
                              <span className="block">Created: {formatDate(lead.createdAt)}</span>
                              <span className="block">Last Activity: {formatDate(lead.updatedAt || lead.createdAt)}</span>
                            </div>
                            <PermissionGate permission="lead_management" action="read">
                              <button
                                onClick={() => handleViewLead(leadId)}
                                className="text-blue-600 hover:text-blue-900"
                                title="View Details"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                            </PermissionGate>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Pagination */}
      {(() => {
        const leadsTotalItems = Number(pagination?.totalLeads || leads?.length || 0);
        const leadsTotalPages = Number(pagination?.totalPages || 1);
        const effectivePaginationLimit = pagination?.limit || itemsPerPage || 10;
        // Show pagination if there's more than one page
        const shouldShowPagination = !leadsLoading && leadsTotalPages > 1;

        return shouldShowPagination ? (
          <div className="bg-white rounded-lg border border-gray-200 p-4 mt-6">
            <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
              {/* Results Count and Limit Selector */}
              <div className="flex items-center space-x-4">
                <div className="text-sm text-gray-700">
                  Showing {formatIndianNumber(((pagination.currentPage || 1) - 1) * effectivePaginationLimit + 1)} to {formatIndianNumber(Math.min((pagination.currentPage || 1) * effectivePaginationLimit, leadsTotalItems))} of {formatIndianNumber(leadsTotalItems)} results
                </div>
                <div className="flex items-center space-x-2">
                  <label className="text-sm text-gray-700">Items per page:</label>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>

              {/* Pagination Controls */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage((pagination.currentPage || 1) - 1)}
                  disabled={pagination.hasPrevPage === false || (pagination.currentPage || 1) <= 1}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>

                {/* Page Numbers */}
                <div className="flex items-center space-x-1">
                  {(() => {
                    const totalPages = pagination.totalPages || 1;
                    const currentPageNum = pagination.currentPage || 1;
                    const pages = [];

                    if (totalPages <= 10) {
                      for (let i = 1; i <= totalPages; i++) {
                        pages.push(i);
                      }
                    } else {
                      pages.push(1);

                      if (currentPageNum <= 4) {
                        for (let i = 2; i <= 5; i++) {
                          pages.push(i);
                        }
                        pages.push('ellipsis');
                        pages.push(totalPages);
                      } else if (currentPageNum >= totalPages - 3) {
                        pages.push('ellipsis');
                        for (let i = totalPages - 4; i <= totalPages; i++) {
                          pages.push(i);
                        }
                      } else {
                        pages.push('ellipsis');
                        for (let i = currentPageNum - 1; i <= currentPageNum + 1; i++) {
                          pages.push(i);
                        }
                        pages.push('ellipsis');
                        pages.push(totalPages);
                      }
                    }

                    return pages.map((page, index) => {
                      if (page === 'ellipsis') {
                        return (
                          <span key={`ellipsis-${index}`} className="px-2 text-gray-500">
                            ...
                          </span>
                        );
                      }

                      return (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${page === currentPageNum
                            ? 'bg-agrosaf-green text-white'
                            : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                          {page}
                        </button>
                      );
                    });
                  })()}
                </div>

                <button
                  onClick={() => setCurrentPage((pagination.currentPage || 1) + 1)}
                  disabled={pagination.hasNextPage === false || (pagination.currentPage || 1) >= (pagination.totalPages || 1)}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        ) : null;
      })()}
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Confirm Delete</h3>
              <button
                onClick={handleCancelDelete}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mb-6">
              <p className="text-sm text-gray-600">
                Are you sure you want to delete this lead? This action cannot be undone.
              </p>
            </div>
            <div className="flex items-center justify-end space-x-3">
              <Button
                variant="outline"
                onClick={handleCancelDelete}
              >
                Cancel
              </Button>
              <PermissionGate permission="lead_management" action="delete">
                <Button
                  variant="primary"
                  onClick={handleConfirmDelete}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Delete
                </Button>
              </PermissionGate>
            </div>
          </div>
        </div>
      )}

      {/* Lead Selection Modal */}
      <LeadSelectionModal
        isOpen={showLeadSelectionModal}
        onClose={() => setShowLeadSelectionModal(false)}
        onSelect={(leadId) => navigate(`/log_activity/${leadId}`)}
      />

      {/* Toast notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            style: {
              background: '#10b981',
            },
          },
          error: {
            duration: 5000,
            style: {
              background: '#f59e0b',
            },
          },
          warning: {
            duration: 5000,
            style: {
              background: '#f59e0b',
            },
          },
        }}
      />
    </div>
  );
};

export default LeadDashboard;
